import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUser } from '../useUser';
import { SectionTitle, Title } from '../../components/typography/Heading';
import { formatPhoneNumber } from '../../utils/phone-utils';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import ButtonText from '../../components/button/button-text/ButtonText';
import VerifyPhoneNumber from '../edit-phone-number/VerifyPhoneNumber';
import { confirmContactDetails, getAuthenticatedUser, resendActivationCode } from '../user-actions';
import { useIsUserEmailActivated } from '../../components/hooks/isUserEmailActivated';
import ThemeContainer from '../../components/theme-container/ThemeContainer';
import { THEMES } from '../../utils/cms-utils';
import styles from './update-contact-info.module.less';
import Loading from '../../components/Loading';
import EditUserEmail from '../edit-user-email/EditUserEmail';
import ButtonWrapper from '../../components/button/button-wrapper/ButtonWrapper';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import LoaderBar from '../../components/loader-bar/LoaderBar';

const UpdateContactInfoContent = () => {
    const user = useUser();
    const { changelog = {} } = user;
    const dispatch = useDispatch();
    const { isUserActivated, canContinueToActivateFacebook } = useIsUserEmailActivated();
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);
    const [hasResentEmailActivationCode, setHasResentEmailActivationCode] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchUserOnSuccess = useCallback(() => {
        dispatch(getAuthenticatedUser(true));
    }, [dispatch]);

    useEffect(() => {
        if (user?.shouldAskForUpdatedContactDetails) {
            trackEvent('update-user-details', 'show');
        }
    }, [user?.shouldAskForUpdatedContactDetails]);

    const isFetchingUser = user.isFetching;
    const { lastEmailChange, lastPhoneNumberChange } = changelog;

    const userPhoneNumber = user.phone_number;

    const confirmDetails = async () => {
        setIsSubmitting(true);
        dispatch(confirmContactDetails())
            .then(() => dispatch(getAuthenticatedUser(true)))
            .finally(() => setIsSubmitting(false));
    };

    const sendNewActivationCode = () => {
        dispatch(resendActivationCode(user));
        trackEvent('update-user-details', 'resend-activation-code');
        setHasResentEmailActivationCode(true);
    };

    const shouldPromptUserToActivateEmailAddress = !isUserActivated && !canContinueToActivateFacebook;

    if (!user) {
        return <LoaderBar />;
    }

    return (
        <>
            {!(isEditingEmail || isEditingPhoneNumber) && (
                <>
                    <SectionTitle tag={'h2'}>Kontaktinformasjon</SectionTitle>
                    <p>Vennligst bekreft at kontaktinformasjonen er riktig før du går videre</p>
                    <div className={styles.contactDetails}>
                        <div className={styles.contactDetail}>
                            <div>
                                <div className={styles.label}>E-post:</div>
                                <div className={styles.existingValue}>{user?.email}</div>
                                {lastEmailChange?.created_at_long && !canContinueToActivateFacebook && (
                                    <p className={styles.changedAt}>
                                        Sist endret:{' '}
                                        <time dateTime={lastEmailChange.created_at}>
                                            {lastEmailChange.created_at_long}
                                        </time>
                                    </p>
                                )}
                            </div>
                            {shouldPromptUserToActivateEmailAddress && (
                                <ThemeContainer mainColor={THEMES.LIGHT_YELLOW} className={styles.activateEmailInfo}>
                                    <p>
                                        Du må bekrefte e-postadressen din. Vi har sendt en aktiveringslenke på e-post
                                        til <strong>{user?.email}</strong>.
                                    </p>
                                    {!user.isSendingActivationCode && hasResentEmailActivationCode ? (
                                        <p>En ny aktiveringslenke er sendt til e-posten din</p>
                                    ) : (
                                        <p>
                                            Fikk du ingen e-post?{' '}
                                            <ButtonText
                                                tightFocus
                                                disabled={!!user.isSendingActivationCode}
                                                onClick={sendNewActivationCode}
                                            >
                                                {user.isSendingActivationCode
                                                    ? 'Sender e-posten …'
                                                    : 'Send e-posten på nytt'}
                                            </ButtonText>
                                        </p>
                                    )}
                                </ThemeContainer>
                            )}
                            {isFetchingUser && <Loading size={'30px'} />}
                            <ButtonText onClick={() => setIsEditingEmail(true)}>Endre e-post</ButtonText>
                        </div>

                        <div className={styles.contactDetail}>
                            <div>
                                <div className={styles.label}>Mobilnummer:</div>
                                <div className={styles.existingValue}>
                                    {userPhoneNumber ? formatPhoneNumber(userPhoneNumber) : ''}
                                    {userPhoneNumber && !user.phone_number_verified_at && <> (Ikke&nbsp;verifisert)</>}
                                    {!userPhoneNumber && <span>Mangler mobilnummer</span>}
                                </div>
                            </div>
                            {isFetchingUser && <Loading size={'30px'} />}
                            <ButtonText onClick={() => setIsEditingPhoneNumber(true)}>
                                {userPhoneNumber ? 'Endre' : 'Legg til'} mobilnummer
                            </ButtonText>
                            {lastPhoneNumberChange?.created_at_long && (
                                <p className={styles.changedAt}>
                                    Sist endret:{' '}
                                    <time dateTime={lastPhoneNumberChange.created_at}>
                                        {lastPhoneNumberChange.created_at_long}
                                    </time>
                                </p>
                            )}
                        </div>
                    </div>
                    <ButtonWrapper className={styles.confirmButton}>
                        <RoundedButtonPrimary onClick={confirmDetails} disabled={isSubmitting}>
                            Bekreft
                        </RoundedButtonPrimary>
                    </ButtonWrapper>
                </>
            )}

            {isEditingEmail && (
                <>
                    <Title tag={'h2'}>Endrer e-postadresse</Title>
                    <EditUserEmail
                        onSuccess={() => {
                            fetchUserOnSuccess();
                            setIsEditingEmail(false);
                        }}
                        onAbort={() => setIsEditingEmail(false)}
                    />
                </>
            )}

            {isEditingPhoneNumber && (
                <>
                    <Title tag={'h2'}>Endrer mobilnummer</Title>
                    <VerifyPhoneNumber
                        onSuccess={() => {
                            fetchUserOnSuccess();
                            setIsEditingPhoneNumber(false);
                        }}
                        onAbort={() => setIsEditingPhoneNumber(false)}
                    />
                </>
            )}
        </>
    );
};

export default UpdateContactInfoContent;
